import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useBrandInfo } from '~/hooks/useBrandInfo';
import useResponsive from '~/hooks/useResponsive';

import HowtoCard from './HowtoCard';
import VideoArticleCard from './VideoArticleCard';

/* ── Item types ── */

export interface HowToItemConfig {
  type: 'howto';
  title?: string;
  description?: string;
  articleUrl?: string;
  displayVideoArticleCard?: boolean;
  aiImageApp?: string | null;
}

export interface VideoArticleItemConfig {
  type: 'video';
  titleI18nKey: string;
  descriptionI18nKey: string;
  articleUrl?: string;
  videoUrl?: string;
}

export type CarouselItem = HowToItemConfig | VideoArticleItemConfig;

interface HowToCarouselProps {
  items: CarouselItem[];
}

const CARDS_PER_PAGE = 2;
const SWIPE_THRESHOLD = 60;

/* ── Carousel wrapper ── */

export default function HowToCarousel({ items }: HowToCarouselProps) {
  const { t } = useTranslation('sites', { keyPrefix: 'howtoCard' });
  const { howToArticleUrl } = useBrandInfo();
  const { isMobile } = useResponsive();
  const isMobileView = isMobile();

  const cardsPerPage = isMobileView ? 1 : CARDS_PER_PAGE;
  const totalPages = Math.ceil(items.length / cardsPerPage);
  const needsCarousel = items.length > cardsPerPage;

  const [currentPage, setCurrentPage] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragX, setDragX] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages - 1));
  }, [totalPages]);

  const goTo = useCallback(
    (page: number) => {
      setCurrentPage(Math.max(0, Math.min(page, totalPages - 1)));
      setDragX(0);
    },
    [totalPages],
  );

  const prev = useCallback(() => goTo(currentPage - 1), [currentPage, goTo]);
  const next = useCallback(() => goTo(currentPage + 1), [currentPage, goTo]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!needsCarousel) return;
    setIsDragging(true);
    setStartX(e.clientX);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setDragX(e.clientX - startX);
  };

  const onPointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (Math.abs(dragX) > SWIPE_THRESHOLD) {
      dragX < 0 ? next() : prev();
    } else {
      setDragX(0);
    }
  };

  const renderCard = (item: CarouselItem, index: number) => (
    <Box key={index} sx={{ flex: '1 1 0', minWidth: 0 }}>
      {item.type === 'video' ? (
        <VideoArticleCard
          titleI18nKey={item.titleI18nKey}
          descriptionI18nKey={item.descriptionI18nKey}
          articleUrl={item.articleUrl}
          videoUrl={item.videoUrl}
          embedded
        />
      ) : (
        <HowtoCard
          title={item.title}
          description={item.description}
          articleUrl={item.articleUrl}
          displayVideoArticleCard={item.displayVideoArticleCard}
          aiImageName={item.aiImageApp ?? ''}
          embedded
        />
      )}
    </Box>
  );

  /* ── Shared header ── */
  const header = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="h2" data-testid="howto-carousel-header">
        {t('header')}
      </Typography>
      {howToArticleUrl && (
        <Link
          href={howToArticleUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={{ fontSize: 14, textDecoration: 'none' }}
          data-testid="howto-carousel-learn-all-link"
        >
          {t('learnAllLink')}
        </Link>
      )}
    </Stack>
  );

  /* ── Static layout when items fit on one page ── */
  if (!needsCarousel) {
    return (
      <Stack spacing={2} sx={{ mt: 4 }} data-testid="howto-carousel">
        {header}
        <Stack direction="row" spacing={2}>
          {items.map(renderCard)}
        </Stack>
      </Stack>
    );
  }

  /* ── Build page groups ── */
  const pages: CarouselItem[][] = [];
  for (let p = 0; p < totalPages; p++) {
    pages.push(items.slice(p * cardsPerPage, (p + 1) * cardsPerPage));
  }
  const trackWidth = trackRef.current?.offsetWidth || 1;
  const offset = -(currentPage * trackWidth) + dragX;

  return (
    <Stack spacing={2} sx={{ mt: 4 }} data-testid="howto-carousel">
      {header}

      {/* Viewport */}
      <Box
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        sx={{ overflow: 'hidden', touchAction: 'pan-y' }}
      >
        {/* Sliding track */}
        <Stack
          direction="row"
          sx={{
            willChange: 'transform',
            transform: `translateX(${offset}px)`,
            transition: isDragging
              ? 'none'
              : 'transform 0.35s cubic-bezier(.4,0,.2,1)',
          }}
        >
          {pages.map((group, pageIdx) => (
            <Stack
              key={pageIdx}
              direction="row"
              spacing={2}
              sx={{ flex: '0 0 100%', minWidth: '100%' }}
            >
              {group.map(renderCard)}
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Pagination: arrows + dots */}
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={1.5}
        sx={{ position: 'relative', zIndex: 1, pb: isMobileView ? 2 : 0 }}
      >
        <IconButton
          onClick={prev}
          disabled={currentPage === 0}
          size="small"
          aria-label="Previous"
          data-testid="howto-carousel-prev"
        >
          <ChevronLeftIcon />
        </IconButton>

        <Stack direction="row" alignItems="center" spacing={1}>
          {Array.from({ length: totalPages }).map((_, i) => (
            <Box
              key={i}
              component="button"
              onClick={() => goTo(i)}
              aria-label={`Page ${i + 1}`}
              data-testid={`howto-carousel-dot-${i}`}
              sx={{
                width: i === currentPage ? 40 : 8,
                height: 8,
                borderRadius: 999,
                border: 'none',
                p: 0,
                cursor: 'pointer',
                bgcolor: i === currentPage ? 'primary.main' : 'action.disabled',
                transition: 'all 0.3s cubic-bezier(.4,0,.2,1)',
              }}
            />
          ))}
        </Stack>

        <IconButton
          onClick={next}
          disabled={currentPage === totalPages - 1}
          size="small"
          aria-label="Next"
          data-testid="howto-carousel-next"
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
}
